/**
 * Survol croisé de la barre et du tableau du décompte (DEV-621, maquette v2
 * du 16/10).
 *
 * INTENTION. Le client survole un segment ou une entrée de légende de la
 * barre : les lignes du tableau du même devenir s'éclairent ; il survole une
 * ligne : son segment et son entrée de légende s'éclairent. Le parent qui
 * réunit la barre et le tableau fournit l'état (`fournirDevenirSurvole`) ;
 * sans parent (page des sources), chaque composant garde un état local et la
 * barre n'éclaire que sa légende.
 *
 * Le survol suit le pointeur (souris, stylet) et le focus clavier ; un
 * toucher ne pose ni ne laisse aucun éclairage (sur un écran tactile, il n'y
 * a pas de « sortie » pour l'éteindre).
 */
import { inject, provide, ref, type InjectionKey, type Ref } from "vue"
import type { DevenirPlace } from "@core/composables/useCompanyPlafond"

const CLE_DEVENIR_SURVOLE: InjectionKey<Ref<DevenirPlace | null>> =
    Symbol("devenirSurvole")

/** Fournit l'état partagé du survol aux composants enfants ; le rend */
export function fournirDevenirSurvole(): Ref<DevenirPlace | null> {
    const etat = ref<DevenirPlace | null>(null)
    provide(CLE_DEVENIR_SURVOLE, etat)
    return etat
}

/** État du survol (partagé s'il est fourni, local sinon) et ses actions */
export function useDevenirSurvole() {
    const devenirSurvole =
        inject(CLE_DEVENIR_SURVOLE, null) ?? ref<DevenirPlace | null>(null)

    /** Éclaire un devenir */
    function survoler(devenir: DevenirPlace) {
        devenirSurvole.value = devenir
    }

    /** Éteint l'éclairage */
    function quitter() {
        devenirSurvole.value = null
    }

    /**
     * Entrée du pointeur : éclaire le devenir, sauf au toucher
     * @param event - événement pointeur
     * @param devenir - devenir de l'élément survolé
     */
    function entrerPointeur(event: PointerEvent, devenir: DevenirPlace) {
        if (event.pointerType === "touch") return
        survoler(devenir)
    }

    /**
     * Sortie du pointeur : éteint l'éclairage, sauf au toucher
     * @param event - événement pointeur
     */
    function sortirPointeur(event: PointerEvent) {
        if (event.pointerType === "touch") return
        quitter()
    }

    /** Vrai : le devenir est éclairé */
    const estEclaire = (devenir: DevenirPlace): boolean =>
        devenirSurvole.value === devenir

    return {
        devenirSurvole,
        survoler,
        quitter,
        entrerPointeur,
        sortirPointeur,
        estEclaire,
    }
}
